/**
 * One-shot install hint banner.
 *
 * - Android Chrome: shows a button that fires the deferred
 *   beforeinstallprompt event.
 * - iOS Safari: shows a manual instruction (Share → Add to Home Screen).
 * - Already standalone: hides itself.
 *
 * Dismiss state lives in localStorage so we don't nag — once dismissed,
 * it stays dismissed for that browser. Cleared on logout (auth.tsx).
 */

import { useEffect, useState } from 'react';
import { canPromptInstall, isIosSafari, isStandalone, promptInstall } from '../pwa';

const DISMISS_KEY = 'install_hint_dismissed';

export default function InstallHint() {
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    // Wait a beat after mount so we don't blast a banner on first paint.
    const t = setTimeout(() => {
      const ios = isIosSafari();
      const canPrompt = canPromptInstall();
      if (ios || canPrompt) {
        setIosMode(ios);
        setVisible(true);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted' || outcome === 'unavailable') dismiss();
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:max-w-sm bg-tarot-card border border-tarot-purple/40 rounded-xl p-4 shadow-2xl z-50">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-tarot-gold font-medium text-sm mb-1">
            加到主畫面，體驗更順
          </div>
          {iosMode ? (
            <p className="text-tarot-muted text-xs leading-relaxed">
              點下方
              <span className="inline-block px-1.5 py-0.5 mx-1 rounded bg-white/10">
                分享 ⬆︎
              </span>
              → 選「加入主畫面」即可像 app 一樣使用。
            </p>
          ) : (
            <p className="text-tarot-muted text-xs leading-relaxed">
              安裝為 app 後可全螢幕使用，啟動更快。
            </p>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-tarot-muted hover:text-tarot-text text-lg leading-none"
          aria-label="關閉提示"
        >
          ×
        </button>
      </div>
      {!iosMode && (
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={dismiss} className="text-xs text-tarot-muted px-3 py-1.5">
            稍後
          </button>
          <button
            onClick={install}
            className="text-xs btn-primary px-4 py-1.5"
          >
            安裝
          </button>
        </div>
      )}
    </div>
  );
}
